import React, { useState, useEffect } from 'react';
import './styles.css';
import { useTutorialsContext } from '../../TutorialsContext';

const CoinWithNumber = ({
  number,
  style = {},
  label = '',
  show = true,
  alt = "coin",
  fontSize = '1.1em',
  iconSize = '1em',
  direction = 'row',
  gap = 4,
}: {
  number: React.ReactNode;
  style?: React.CSSProperties;
  label?: string;
  show?: boolean;
  alt?: string;
  fontSize?: string;
  iconSize?: string;
  direction?: 'row' | 'column';
  gap?: number;
}) => {
  if (!show) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: direction,
        alignItems: 'center',
        gap,
        fontWeight: 700,
        fontSize,
        ...style,
      }}
      aria-label={label}
    >
      {number}
      <img
        src={require('../../../../assets/coin.svg').default}
        alt={alt}
        style={{ height: iconSize, verticalAlign: 'middle', marginLeft: 2 }}
      />
    </span>
  );
};

const ArrowLabel = ({
  from,
  to,
  label,
  number,
  show,
  style = {},
  fontSize = '1.1em',
  iconSize = '1em',
  arrow = '→',
  color = '#1976d2',
  numberColor = '#1976d2',
  delay = 0,
}: {
  from?: string;
  to?: string;
  label?: string;
  number: number;
  show: boolean;
  style?: React.CSSProperties & { left?: number; top?: number };
  fontSize?: string;
  iconSize?: string;
  arrow?: string;
  color?: string;
  numberColor?: string;
  delay?: number;
}) => {
  // Simple fade-in
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(t);
    }
  }, [show, delay]);
  return (
    <div
      style={{
        position: 'absolute',
        left: style.left,
        top: style.top,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.7s',
        pointerEvents: 'none',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          fontSize,
          color,
          fontWeight: 700,
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 8,
          padding: '6px 14px 6px 14px',
          boxShadow: '0 1px 4px rgba(25,118,210,0.07)',
          minWidth: 90,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {from && <span>{from}</span>}
          <span style={{ fontSize: '1.2em', color: '#888', margin: '0 2px' }}>{arrow}</span>
          {to && <span>{to}</span>}
          {label && <span style={{ fontWeight: 400, color: '#444', marginLeft: 6 }}>{label}</span>}
        </span>
        <span
          style={{
            marginTop: 2,
            fontWeight: 700,
            color: numberColor,
            fontSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CoinWithNumber number={number} iconSize={iconSize} fontSize={fontSize} />
        </span>
      </div>
    </div>
  );
};

// AnimatedNumber: animates a number from start to end, coloring green when increasing, red when decreasing
const AnimatedNumber = ({ value, duration = 800, style = {}, ...props }: { value: number; duration?: number; style?: React.CSSProperties }) => {
  const [display, setDisplay] = useState(value);
  const [color, setColor] = useState<string | undefined>(undefined);
  const prevValue = React.useRef(value);

  useEffect(() => {
    console.log('AnimatedNumber received value:', value);
    if (prevValue.current === value) return;
    let frame: number;
    let start = prevValue.current;
    let end = value;
    let step = start < end ? 1 : -1;
    let totalSteps = Math.abs(end - start);
    let interval = totalSteps === 0 ? duration : duration / totalSteps;
    let current = start;
    setColor(end > start ? '#388e3c' : '#d32f2f');
    function animate() {
      if (current === end) {
        setColor(undefined);
        prevValue.current = end;
        return;
      }
      current += step;
      setDisplay(current);
      frame = window.setTimeout(animate, interval);
    }
    animate();
    return () => {
      clearTimeout(frame);
    };
    // eslint-disable-next-line
  }, [value, duration]);

  return (
    <span style={{ color: color, transition: 'color 0.2s', ...style }} {...props}>{display}</span>
  );
};

const ScenarioOne = () => {
  const { setDisabled } = useTutorialsContext();

  // Animation steps
  const [showTitlePlayers, setShowTitlePlayers] = useState(false);
  const [showInvestArrows, setShowInvestArrows] = useState(false);
  const [showPool, setShowPool] = useState(false);
  const [showReceiveArrows, setShowReceiveArrows] = useState(false);
  const [showBottomArrows, setShowBottomArrows] = useState(false);
  const [showBalances, setShowBalances] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);

  // Add state variables for each pool stage
  const [showAddition, setShowAddition] = useState(false);
  const [showPoolGrowth, setShowPoolGrowth] = useState(false);
  const [showDivideEqually, setShowDivideEqually] = useState(false);

  // Update stage descriptions with different explanations for each pool stage
  const stageDescriptions = [
    'Each player has 30 coins in the round.',
    'Players can invest 0-30 coins into the pool.',
    'The investments are added together in the pool (12 + 8 = 20 coins).',
    'The total investment will grow by 1.5x (20 × 1.5 = 30).',
    'The pool is divided equally between both players (30 ÷ 2 = 15 each).',
    'Each of them gain 15 coins, and added up to their not invested coins.',
    ''
  ];

  // Define a speedMultiplier variable
  const speedMultiplier = 3;
  /** CSS opacity transition on scenario elements (must match styles). */
  const SCENARIO_FADE_MS = 700;

  // Initial state for introduction text visibility
  const [showIntroduction, setShowIntroduction] = useState(false);

  // Update useEffect to remove typing animation calls
  useEffect(() => {
    setDisabled(true);
    // Step 1: Title, players, and introduction text
    const t1 = setTimeout(() => {
      setShowTitlePlayers(true);
      setShowIntroduction(true); // Show introduction text with animation
      setCurrentStage(0);
      // Remove typing animation call
      // typeText(stageDescriptions[0]);
    }, 600); // Further reduced delay for first animation

    // Step 2: Show investing message, animate reduction, and show investment arrows
    const t2 = setTimeout(() => {
      setAAction('Investing 12 coins...');
      setBAction('Investing 8 coins...');
      setShowInvestArrows(true);
      setCurrentStage(1); // Show "Players can invest 0-30 coins into the pool."
      let a = 30, b = 30;
      setAColor('#d32f2f');
      setBColor('#d32f2f');
      const interval = setInterval(() => {
        let changed = false;
        if (a > 18) { 
          a--; 
          setATotal(a); 
          changed = true; 
        }
        if (b > 22) { 
          b--; 
          setBTotal(b); 
          changed = true; 
        }
        if (!changed) {
          clearInterval(interval);
          setTimeout(() => {
            setAColor(undefined); 
            setBColor(undefined); 
            setAAction(null); 
            setBAction(null); 
            setShowInvestArrows(false);
          }, 600 * speedMultiplier);
        }
      }, 80 * speedMultiplier); // Faster animation
    }, 1200 * speedMultiplier);

    // Step 3: Show pool with original timing for first stage, slower for others
    const t3 = setTimeout(() => {
      setShowPool(true);
      setCurrentStage(2); // Show "The investments are added together in the pool (12 + 8 = 20 coins)."
      setShowAddition(true); // Show addition stage
    }, 3200 * speedMultiplier); // Keep original timing
    const t4 = setTimeout(() => {
      setShowPoolGrowth(true); // Show pool growth stage
      setCurrentStage(3); // Show "The total investment will grow by 1.5x (20 × 1.5 = 30)."
    }, 4200 * speedMultiplier); // Slower timing
    const t5 = setTimeout(() => {
      setShowDivideEqually(true); // Show divide equally stage
      setCurrentStage(4); // Show "The pool is divided equally between both players (30 ÷ 2 = 15 each)."
    }, 5200 * speedMultiplier); // Slower timing

    // Step 4: Show receiving message, animate increase, and show receive arrows - shifted to maintain timing
    const t6 = setTimeout(() => {
      setAAction('Receiving 15 coins...');
      setBAction('Receiving 15 coins...');
      setShowReceiveArrows(true);
      setCurrentStage(5); // Show "Each of them gain 15 coins, and added up to their total balance."
      let a = 18, b = 22;
      setAColor('#388e3c');
      setBColor('#388e3c');
      const interval = setInterval(() => {
        let changed = false;
        if (a < 33) { 
          a++; 
          setATotal(a); 
          changed = true; 
        }
        if (b < 37) { 
          b++; 
          setBTotal(b); 
          changed = true; 
        }
        if (!changed) {
          clearInterval(interval);
          // Ensure final values are set correctly
          setATotal(33);
          setBTotal(37);
          setTimeout(() => {
            setAColor(undefined); 
            setBColor(undefined); 
            setAAction(null); 
            setBAction(null); 
            setShowReceiveArrows(false);
          }, 600 * speedMultiplier);
        }
      }, 80 * speedMultiplier); // Faster animation
    }, 6200 * speedMultiplier); // Shifted to maintain timing difference

    // Step 5: Show bottom arrows - shifted to maintain timing
    const t7 = setTimeout(() => {
      setShowBottomArrows(true);
      setCurrentStage(6); // Show empty string (final stage)
    }, 8500 * speedMultiplier); // Shifted to maintain timing difference

    // Step 6: Show balances - reduced delay to appear sooner
    const t8 = setTimeout(() => {
      setShowBalances(true);
      setCurrentStage(6); // Keep same stage
    }, 7000 * speedMultiplier); // Reduced from 10500 to 7000 for faster appearance

    // Step 7: Show explanation - reduced delay to appear sooner
    const t9 = setTimeout(() => {
      setShowExplanation(true);
      setCurrentStage(6); // Keep same stage
    }, 8000 * speedMultiplier); // Reduced from 11500 to 8000 for faster appearance

    // Step 8: Enable Proceed after explanation fade-in completes
    const t10 = setTimeout(() => {
      setDisabled(false);
    }, 8000 * speedMultiplier + SCENARIO_FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      clearTimeout(t6);
      clearTimeout(t7);
      clearTimeout(t8);
      clearTimeout(t9);
      clearTimeout(t10);
    };
  }, []);

  // Single total coin number for each player
  const [aTotal, setATotal] = useState(30); // Player A starts with 30
  const [bTotal, setBTotal] = useState(30); // Player B starts with 30
  const [aColor, setAColor] = useState<string | undefined>(undefined);
  const [bColor, setBColor] = useState<string | undefined>(undefined);

  // Add state for action messages
  const [aAction, setAAction] = useState<string | null>(null);
  const [bAction, setBAction] = useState<string | null>(null);

  // Remove poolStage state and its logic

  // Layout constants for annotation positions
  const containerWidth = '100%';
  const manBalancePos = { x: 0, y: 260 };
  const womanBalancePos = { x: 0, y: 260 };

  // Implement logic to update coin values
  // useEffect(() => {
  //   // Example logic to update coin values
  //   const updateCoins = () => {
  //     setATotal(prev => {
  //       const newTotal = prev + 3; // Example increment
  //       console.log('Updating Player A Total:', newTotal);
  //       return newTotal;
  //     });
  //     setBTotal(prev => {
  //       const newTotal = prev + 7; // Example increment
  //       console.log('Updating Player B Total:', newTotal);
  //       return newTotal;
  //     });
  //   };
  //   const coinUpdateInterval = setInterval(updateCoins, 10000 * speedMultiplier); // Update every 10 seconds
  //   return () => clearInterval(coinUpdateInterval);
  // }, []);

  // Add cleanup function to ensure all intervals are cleared
  useEffect(() => {
    return () => {
      // Clear any remaining intervals when component unmounts
      if (typeof window !== 'undefined') {
        const intervals = window.setInterval(() => {}, 0);
        for (let i = 0; i < intervals; i++) {
          clearInterval(i);
        }
      }
    };
  }, []);

  return (
    <div
      className="scenario-container"
      style={{
        position: 'relative',
        minHeight: '100%',
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        // background: 'linear-gradient(180deg, #f7fbff 0%, #e3f2fd 100%)',
        borderRadius: 24,
        // boxShadow: '0 4px 24px rgba(30,60,120,0.08)',
        padding: '5px 32px 10px 32px',
        overflow: 'visible',
        transition: 'background 1s',
      }}
    >
      {/* Title */}
      <h2
        className="scenario-title"
        style={{
          textAlign: 'center',
          color: '#1976d2',
          fontWeight: 800,
          fontSize: '2.2em',
          marginTop: 0,
          marginBottom: 2,
          letterSpacing: 1,
          opacity: showTitlePlayers ? 1 : 0,
          transform: showTitlePlayers ? 'translateY(0)' : 'translateY(-20px)',
          transition: `opacity ${0.7 * speedMultiplier}s, transform ${0.7 * speedMultiplier}s`,
        }}
      >
        Scenario 1
      </h2>

      {/* Dynamic Text directly below the Title */}
      <p style={{ fontStyle: 'italic', color: '#555', marginTop: 0, marginBottom: '8px', textAlign: 'center', fontSize: '1.3em', paddingBottom: '8px', opacity: showIntroduction ? 1 : 0, transition: 'opacity 0.7s' }}>{stageDescriptions[currentStage]}</p>

      {/* Coin flow arrows and numbers */}
      <ArrowLabel
        from="Player A"
        to="Pool"
        number={12}
        show={showInvestArrows}
        style={{
          left: 325,
          top: 116,
        }}
        arrow="→"
        color="#1976d2"
        numberColor="#1976d2"
        delay={0}
      />
      <ArrowLabel
        from="Pool"
        to="Player B"
        number={8}
        show={showInvestArrows}
        style={{
          left: 760,
          top: 116,
        }}
        arrow="←"
        color="#1976d2"
        numberColor="#1976d2"
        delay={200}
      />
      <ArrowLabel
        from="Player A"
        to="Pool"
        number={15}
        show={showReceiveArrows}
        style={{
          left: 325,
          top: 224,
        }}
        arrow="←"
        color="#388e3c"
        numberColor="#388e3c"
        delay={0}
      />
      <ArrowLabel
        from="Pool"
        to="Player B"
        number={15}
        show={showReceiveArrows}
        style={{
          left: 760,
          top: 224,
        }}
        arrow="→"
        color="#388e3c"
        numberColor="#388e3c"
        delay={200}
      />

      {/* Main scenario body */}
      <div
        className="scenario-body"
        style={{
          display: 'grid',
          gridTemplateColumns: '3fr 0.5fr 1fr 0.5fr 3fr',
          gridTemplateRows: 'auto auto',
          gap: '0 0',
          alignItems: 'center',
          justifyItems: 'center',
          position: 'relative',
          minHeight: 200,
          marginTop: 24,
          width: '100%',
          maxWidth: 2500,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {/* Player A */}
        <div
          className="person"
          style={{
            gridColumn: 1,
            gridRow: 1,
            width: 120,
            textAlign: 'center',
            opacity: showTitlePlayers ? 1 : 0,
            transform: showTitlePlayers ? 'scale(1)' : 'scale(0.95)',
            transition: `opacity ${0.7 * speedMultiplier}s, transform ${0.7 * speedMultiplier}s`,
            zIndex: 2,
          }}
        >

          <p style={{ margin: 5, color: '#1976d2', fontWeight: 700, fontSize: '1em' }}>Player A</p>
          <img
            src={require('../../../../assets/man.svg').default}
            alt="Avatar"
            style={{ width: 64, height: 64, marginBottom: 8 }}
          />
          {/* Ensure AnimatedNumber is used for total coins */}
          <p style={{ margin: 0, fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 4 }}>
            Total Coins: <CoinWithNumber number={aTotal} style={{color: aColor}} />
          </p>
          {aAction && <p style={{ margin: 0, color: aColor, fontWeight: 500, fontSize: '0.98em', minHeight: 22 }}>{aAction}</p>}
        </div>

        {/* Pool (center) */}
        <div
          className="pool"
          style={{
            gridColumn: 3,
            gridRow: 1,
            width: 220,
            minHeight: 90,
            background: 'rgba(25, 118, 210, 0.08)',
            border: '2px dashed #1976d2',
            borderRadius: 18,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            opacity: showPool ? 1 : 0,
            transition: `opacity ${0.7 * speedMultiplier}s`,
            margin: '40px 20px 0 20px', // Move pool 20px lower
          }}
        >
          <span style={{ color: '#1976d2', fontWeight: 700, fontSize: '1.1em' }}>Pool</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, marginTop: 8, marginBottom: 4 }}>
            {showAddition && <span style={{ color: '#1976d2', fontWeight: 600, fontSize: '1em', opacity: showAddition ? 1 : 0, transition: 'opacity 0.7s' }}>Sum-up: <b>12 + 8 = 20</b></span>}
            {showPoolGrowth && <span style={{ color: '#388e3c', fontWeight: 600, fontSize: '1em', opacity: showPoolGrowth ? 1 : 0, transition: 'opacity 0.7s' }}>Pool growth: <b>20 × 1.5 = 30</b></span>}
            {showDivideEqually && <span style={{ color: '#d17b00', fontWeight: 600, fontSize: '1em', opacity: showDivideEqually ? 1 : 0, transition: 'opacity 0.7s' }}>Equal division: <b>30 / 2 = 15</b></span>}
          </div>
          {/* <span style={{ color: '#888', fontSize: '0.92em', marginTop: 2, display: 'block', textAlign: 'center' }}>
            (Sum investments × 3/4, equal reward for each)
          </span> */}
        </div>

        {/* Player B */}
        <div
          className="person"
          style={{
            gridColumn: 5,
            gridRow: 1,
            width: 120,
            textAlign: 'center',
            opacity: showTitlePlayers ? 1 : 0,
            transform: showTitlePlayers ? 'scale(1)' : 'scale(0.95)',
            transition: `opacity ${0.7 * speedMultiplier}s, transform ${0.7 * speedMultiplier}s`,
            zIndex: 2,
          }}
        >

          <p style={{ margin: 5, color: '#1976d2', fontWeight: 700, fontSize: '1em' }}>Player B</p>
          <img
            src={require('../../../../assets/woman.svg').default}
            alt="Avatar"
            style={{ width: 64, height: 64, marginBottom: 8 }}
          />
          {/* Ensure AnimatedNumber is used for total coins */}
          <p style={{ margin: 0, fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 4 }}>
            Total Coins: <CoinWithNumber number={bTotal} style={{color: bColor}} />
          </p>
          {bAction && <p style={{ margin: 0, color: bColor, fontWeight: 500, fontSize: '0.98em', minHeight: 22 }}>{bAction}</p>}
        </div>
      </div>

      {/* Earning label (center, below pool) */}
      {/* <div
        style={{
          width: '100%',
          textAlign: 'center',
          marginTop: 16,
          opacity: showBalances ? 1 : 0,
          transform: showBalances ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s, transform 0.7s',
          zIndex: 2,
        }}
      >
        <p className="earning-label" style={{ fontSize: '1.1em', margin: 0 }}>
          Earning: [(12 + 8) × <span className="green" style={{ color: '#388e3c', fontWeight: 700 }}>1.5</span>] / <span className="blue" style={{ color: '#1976d2', fontWeight: 700 }}>2</span>
        </p>
      </div> */}

      {/* Balances after distribution */}
      <div
        className="balances"
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          maxWidth: 900,
          margin: '32px auto 0 auto',
          opacity: showBalances ? 1 : 0,
          transform: showBalances ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s, transform 0.7s',
          pointerEvents: 'none',
        }}
      >
        <div
          className="balance-box"
          style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(25,118,210,0.08)',
            padding: '12px 24px',
            fontWeight: 700,
            color: '#1976d2',
            fontSize: '1.1em',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          Total Balance: <CoinWithNumber number={<AnimatedNumber value={33} duration={200} />} />
          <span style={{ color: '#388e3c', fontWeight: 700, marginLeft: 8, fontSize: '1.1em' }}>↑ +3</span>
        </div>
        <div
          className="balance-box"
          style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(25,118,210,0.08)',
            padding: '12px 24px',
            fontWeight: 700,
            color: '#1976d2',
            fontSize: '1.1em',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          Total Balance: <CoinWithNumber number={<AnimatedNumber value={37} duration={200} />} />
          <span style={{ color: '#388e3c', fontWeight: 700, marginLeft: 8, fontSize: '1.1em' }}>↑ +7</span>
        </div>
      </div>

      {/* Explanation */}
      <p
        className="explanation"
        style={{
          fontSize: "1.1em",
          marginTop: 16,
          textAlign: 'center',
          maxWidth: 800,
          marginLeft: 'auto',
          marginRight: 'auto',
          opacity: showExplanation ? 1 : 0,
          transform: showExplanation ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s, transform 0.7s',
          background: 'rgba(255,255,255,0.85)',
          borderRadius: 10,
          padding: '16px 24px',
          boxShadow: '0 2px 8px rgba(25,118,210,0.06)',
        }}
      >
        <strong>Explanation:</strong> Both players invest in the pool. The total investment is multiplied by 1.5, and the reward is split equally between them, regardless of their individual contributions. Your final reward includes your round earnings plus any not invested coins.
      </p>
    </div>
  );
};

export default ScenarioOne;
